/**
 * Error Boundary Component - Catches React component errors gracefully
 * Uses shadcn UI components for consistent styling
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { error as logError, logToErrorTracking } from '@/utils/logger';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to error reporting service (e.g., Sentry, LogRocket)
    logError('ErrorBoundary caught an error:', error, errorInfo);
    logToErrorTracking(error, { errorInfo });
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  override render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="max-w-md w-full border-destructive/30">
            <CardHeader>
              <div className="flex items-center space-x-3">
                <AlertTriangle className="w-8 h-8 text-destructive" />
                <CardTitle>Something went wrong</CardTitle>
              </div>
              <CardDescription>
                We&apos;re sorry, but something unexpected happened. Please try refreshing the page.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {import.meta.env.DEV && this.state.error && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Error details (dev only)</AlertTitle>
                  <AlertDescription>
                    <details className="mt-2">
                      <summary className="text-sm cursor-pointer mb-2">
                        Show error stack
                      </summary>
                      <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-40 text-foreground">
                        {this.state.error.toString()}
                        {this.state.errorInfo?.componentStack}
                      </pre>
                    </details>
                  </AlertDescription>
                </Alert>
              )}
              <div className="flex space-x-3">
                <Button onClick={this.handleReset}>
                  Try Again
                </Button>
                <Button variant="outline" onClick={() => {
                  // Reload page to recover from error state
                  window.location.reload();
                }}>
                  Refresh Page
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
